/* global customElements */
import {LitElement, html} from 'lit';
import {ElementUtil} from '../utils/elementUtil.js';
import {pageRegistry} from '../utils/pageRegistry.js';
import {findWorkspaceCapableElements} from '../utils/workspaceStateUtil.js';

class MainContent extends LitElement {
	
	static get properties() {
		return {
			src: {type: String},
			loading: {type: Boolean},
			bodyStyle: {type: String}
		};
	}

	constructor() {
		super();
		this.src = null;	
		this.loading = false;
		this.bodyStyle = '';
		this.panesContainer = null;
		this.loadMaskElement = null;
		
		LitElement.disableWarning = true;
		
		var mainContent = this;
		setTimeout(function() {
			mainContent.ensureHost();
			pageRegistry.setContentHost(mainContent);
		});
	}
	
	render() {		
		return html`		
			<slot></slot>
		`;
	}
			
	createRenderRoot() {
		return this;	
	}

	ensureHost() {
		var contentElem = this;
		if (contentElem.panesContainer) {
			return contentElem.panesContainer;
		}

		var hostStyle = contentElem.bodyStyle || '';
		if (hostStyle.indexOf('min-width') === -1) {
			hostStyle += '; min-width: 0';
		}
		if (hostStyle.indexOf('position') === -1) {
			hostStyle += '; position: relative';
		}
		if (hostStyle.indexOf('height') === -1) {
			hostStyle += '; height: 100%';
		}

		contentElem.panesContainer = ElementUtil.create({
			tag: 'div',
			'class': 'content-panes-host',
			style: hostStyle
		});
		contentElem.append(contentElem.panesContainer);

		contentElem.loadMaskElement = ElementUtil.create({
			tag: 'div',
			'class': 'content-load-mask hide',
			style: 'position: absolute; inset: 0; z-index: 50; background: rgba(255,255,255,0.65); display: flex; align-items: center; justify-content: flex-start; padding-left: 10px;'
		});
		contentElem.panesContainer.append(contentElem.loadMaskElement);

		return contentElem.panesContainer;
	}

	showContentLoading(show) {
		this.ensureHost();
		if (show) {
			this.loadMaskElement.classList.remove('hide');
			this.loadMaskElement.innerHTML = '<p><i class="fas fa-cog fa-2x fa-spin"></i> Loading...</p>';
			this.loadMaskElement.style.display = 'flex';
		} else {
			this.loadMaskElement.classList.add('hide');
			this.loadMaskElement.style.display = 'none';
			this.loadMaskElement.innerHTML = '';
		}
	}

	/**
	 * Legacy API — routes through openOrFocus when item details are available.
	 */
	async loadContent(newSrc) {
		if (!newSrc) {
			return;
		}
		await this.openOrFocus({
			key: newSrc,
			url: newSrc,
			title: newSrc
		});
	}

	/**
	 * Open existing page or create a new keep-alive pane.
	 * @param {object} item - { key, url, title }
	 */
	async openOrFocus(item) {
		var contentElem = this;
		contentElem.ensureHost();

		if (!item || !item.key) {
			return;
		}

		var pageKey = item.key;
		var existing = pageRegistry.getEntry(pageKey);

		if (existing) {
			if (!existing.mounted) {
				await contentElem.mountStagedPage(existing);
			}
			contentElem.showPane(pageKey);
			return;
		}

		await contentElem.createAndShowPane({
			pageKey: pageKey,
			url: item.url,
			title: item.title || pageKey,
			pendingFilters: item.pendingFilters || null
		});
	}

	async createAndShowPane(options) {
		var contentElem = this;
		contentElem.ensureHost();

		var paneElement = contentElem.buildPaneShell(options.pageKey);
		var body = paneElement.querySelector('.page-body');
		var overlayHost = paneElement.querySelector('.page-overlay-host');

		contentElem.panesContainer.append(paneElement);

		pageRegistry.register({
			pageKey: options.pageKey,
			title: options.title,
			url: options.url,
			paneElement: paneElement,
			overlayHost: overlayHost,
			mounted: false,
			pendingFilters: options.pendingFilters || null
		});

		// Pane must be visible before page scripts/components init (Vaadin tabs need layout).
		contentElem.showPane(options.pageKey);

		contentElem.loading = true;
		contentElem.showContentLoading(true);

		try {
			const res = await fetch(options.url);
			var htmlText = await res.text();
			body.innerHTML = htmlText;
			contentElem.runPageScripts(body);
			var entry = pageRegistry.getEntry(options.pageKey);
			if (entry) {
				entry.mounted = true;
				contentElem.applyPendingRestore(entry);
				pageRegistry.notify();
			}
		} finally {
			contentElem.loading = false;
			contentElem.showContentLoading(false);
		}
	}

	async mountStagedPage(entry) {
		var contentElem = this;
		if (!entry.paneElement) {
			entry.paneElement = contentElem.buildPaneShell(entry.pageKey);
			entry.overlayHost = entry.paneElement.querySelector('.page-overlay-host');
			contentElem.panesContainer.append(entry.paneElement);
		}

		var body = entry.paneElement.querySelector('.page-body');

		// Visible before scripts so tab panels and grids can measure layout.
		contentElem.showPane(entry.pageKey);

		contentElem.loading = true;
		contentElem.showContentLoading(true);

		try {
			const res = await fetch(entry.url);
			var htmlText = await res.text();
			body.innerHTML = htmlText;
			contentElem.runPageScripts(body);
			entry.mounted = true;
			contentElem.applyPendingRestore(entry);
			pageRegistry.notify();
		} finally {
			contentElem.loading = false;
			contentElem.showContentLoading(false);
		}
	}

	/**
	 * Eval page scripts with document lookups scoped to this page body so
	 * duplicate ids (e.g. #tabPanel) across keep-alive panes resolve correctly.
	 */
	runPageScripts(pageBody) {
		var scripts = pageBody.getElementsByTagName('script');
		var originalGetElementById = Document.prototype.getElementById;
		var originalQuerySelector = Document.prototype.querySelector;
		var originalQuerySelectorAll = Document.prototype.querySelectorAll;

		Document.prototype.getElementById = function(id) {
			if (this === document && pageBody) {
				var local = pageBody.querySelector('[id="' + id.replace(/"/g, '\\"') + '"]');
				if (local) {
					return local;
				}
			}
			return originalGetElementById.call(this, id);
		};
		Document.prototype.querySelector = function(selector) {
			if (this === document && pageBody) {
				var local = pageBody.querySelector(selector);
				if (local) {
					return local;
				}
			}
			return originalQuerySelector.call(this, selector);
		};
		Document.prototype.querySelectorAll = function(selector) {
			if (this === document && pageBody) {
				var local = pageBody.querySelectorAll(selector);
				if (local && local.length > 0) {
					return local;
				}
			}
			return originalQuerySelectorAll.call(this, selector);
		};

		try {
			for (var n = 0; n < scripts.length; n++) {
				eval(scripts[n].innerHTML);
			}
		} finally {
			Document.prototype.getElementById = originalGetElementById;
			Document.prototype.querySelector = originalQuerySelector;
			Document.prototype.querySelectorAll = originalQuerySelectorAll;
		}
	}

	applyPendingRestore(entry) {
		if (!entry || !entry.pendingFilters) {
			return;
		}
		var filters = entry.pendingFilters;
		var attempts = 0;
		var maxAttempts = 40;

		var tryRestore = function() {
			var pageRoot = pageRegistry.findPageRoot(entry.paneElement);
			if (pageRoot) {
				pageRoot.pendingWorkspaceFilters = filters;
			}
			// Stash on custom elements so tabpanel children can pick filters up during init.
			var body = entry.paneElement && entry.paneElement.querySelector('.page-body');
			if (body) {
				var customElements = body.querySelectorAll('*');
				for (var i = 0; i < customElements.length; i++) {
					var node = customElements[i];
					if (node.tagName && node.tagName.indexOf('DL-') === 0) {
						node.pendingWorkspaceFilters = filters;
					}
				}
			}

			var ready = findWorkspaceCapableElements(entry.paneElement).filter(function(node) {
				return typeof node.restoreWorkspaceState === 'function';
			});
			if (ready.length > 0) {
				entry.pendingFilters = null;
				ready.forEach(function(node) {
					node.pendingWorkspaceFilters = null;
					node.restoreWorkspaceState(filters);
				});
				return;
			}
			attempts++;
			if (attempts < maxAttempts) {
				setTimeout(tryRestore, 50);
			}
		};
		setTimeout(tryRestore, 0);
	}

	buildPaneShell(pageKey) {
		return ElementUtil.create({
			tag: 'div',
			'class': 'page-pane hide',
			'data-page-key': pageKey,
			style: 'position: absolute; inset: 0; overflow: hidden;',
			children: [
				{
					tag: 'div',
					'class': 'page-body',
					style: 'position: absolute; inset: 0; overflow: auto; min-width: 0;'
				},
				{
					tag: 'div',
					'class': 'page-overlay-host',
					style: 'position: absolute; inset: 0; z-index: 20; pointer-events: none;'
				}
			]
		});
	}

	showPane(pageKey) {
		var contentElem = this;
		contentElem.ensureHost();

		var panes = contentElem.panesContainer.querySelectorAll('.page-pane');
		panes.forEach(function(pane) {
			if (pane.getAttribute('data-page-key') === pageKey) {
				pane.classList.remove('hide');
				pane.style.display = 'block';
			} else {
				pane.classList.add('hide');
				pane.style.display = 'none';
			}
		});

		var entry = pageRegistry.getEntry(pageKey);
		if (entry) {
			contentElem.src = entry.url;
		}
		pageRegistry.setActive(pageKey);
	}

	closePage(pageKey) {
		var contentElem = this;
		var entry = pageRegistry.getEntry(pageKey);
		if (!entry) {
			return;
		}

		if (entry.paneElement && entry.paneElement.parentNode) {
			entry.paneElement.parentNode.removeChild(entry.paneElement);
		}
		pageRegistry.remove(pageKey);

		var remaining = pageRegistry.getOpenPages();
		if (remaining.length > 0) {
			var nextKey = remaining[remaining.length - 1].pageKey;
			contentElem.showPane(nextKey);
			contentElem.dispatchPageActivated(nextKey);
		} else {
			contentElem.src = null;
			contentElem.dispatchEvent(new CustomEvent('workspaceempty', { detail: {} }));
		}
	}

	/**
	 * Stage pages for workspace restore without mounting.
	 */
	stageForRestore(workspace) {
		var contentElem = this;
		contentElem.clearAllPages();
		contentElem.ensureHost();

		if (!workspace || !workspace.pages) {
			return;
		}

		pageRegistry.setWorkspaceIdentity(workspace.name || 'Default', workspace.workspaceId || null);

		workspace.pages.forEach(function(page) {
			var paneElement = contentElem.buildPaneShell(page.pageKey);
			var overlayHost = paneElement.querySelector('.page-overlay-host');
			contentElem.panesContainer.append(paneElement);

			pageRegistry.register({
				pageKey: page.pageKey,
				title: page.title || page.pageKey,
				url: page.url,
				paneElement: paneElement,
				overlayHost: overlayHost,
				mounted: false,
				pendingFilters: page.filters || null
			});
		});

		if (workspace.activePageKey && pageRegistry.has(workspace.activePageKey)) {
			contentElem.openOrFocus({
				key: workspace.activePageKey,
				url: pageRegistry.getEntry(workspace.activePageKey).url,
				title: pageRegistry.getEntry(workspace.activePageKey).title
			}).then(function() {
				contentElem.dispatchPageActivated(workspace.activePageKey);
			});
		} else {
			pageRegistry.notify();
		}
	}

	clearAllPages() {
		var contentElem = this;
		contentElem.ensureHost();

		var pages = pageRegistry.getOpenPages().slice();
		pages.forEach(function(page) {
			var entry = pageRegistry.getEntry(page.pageKey);
			if (entry && entry.paneElement && entry.paneElement.parentNode) {
				entry.paneElement.parentNode.removeChild(entry.paneElement);
			}
		});
		pageRegistry.clearAll();
		contentElem.src = null;
	}

	dispatchPageActivated(pageKey) {
		var entry = pageRegistry.getEntry(pageKey);
		if (!entry) {
			return;
		}
		this.dispatchEvent(new CustomEvent('pageactivated', {
			detail: {
				key: pageKey,
				title: entry.title,
				url: entry.url
			},
			bubbles: true
		}));
	}
		
};
customElements.define('dl-main-content', MainContent);
